/**
 * 全局租户模式（tenant=all）
 *
 * 需求：URL 带 tenant=all 时
 * 1. 顶部一级菜单只保留 code = PlatformOverview；
 * 2. 该一级菜单下只保留指定子菜单；
 * 3. 隐藏头部租户下拉。
 *
 * 约定：以 URL 为唯一依据，不做任何本地缓存。
 * 为了让参数在路由跳转中不丢，所有构造子应用地址的地方统一走 withTenantParam：
 * 当前 URL 带 tenant=all 就透传，否则不带任何 tenant 参数。
 *
 * 注意：khlinks 走 hash 路由，query 参数可能出现在 location.search，
 * 也可能出现在 location.hash 里（如 /#/khlinks/overview/device-count?tenant=all），
 * 所以两处都要解析。
 */

export const GLOBAL_TENANT_QUERY_KEY = "tenant";
export const GLOBAL_TENANT_ALL = "all";

/** 全局租户模式下保留的一级菜单 code */
export const GLOBAL_TENANT_ROOT_CODE = "PlatformOverview";

/** 全局租户模式下，该一级菜单下保留的子菜单 code */
export const GLOBAL_TENANT_MENU_CODES = [
  "device/DashBoard",
  "link/DashBoard",
  "media/DashBoard",
  "Dashboard/MessageGateway",
];

const getHashQueryString = (hash: string) => {
  const index = hash.indexOf("?");
  return index === -1 ? "" : hash.slice(index + 1);
};

/**
 * 读取 tenant 参数值，支持 search 与 hash 两种位置
 * @returns 参数值；未携带该参数时返回 null
 */
export const getTenantQueryValue = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has(GLOBAL_TENANT_QUERY_KEY)) {
    return searchParams.get(GLOBAL_TENANT_QUERY_KEY);
  }

  const hashParams = new URLSearchParams(getHashQueryString(window.location.hash));
  if (hashParams.has(GLOBAL_TENANT_QUERY_KEY)) {
    return hashParams.get(GLOBAL_TENANT_QUERY_KEY);
  }

  return null;
};

/**
 * 是否处于全局租户模式（只看当前 URL，不缓存）
 */
export const isGlobalTenantMode = (): boolean =>
  getTenantQueryValue() === GLOBAL_TENANT_ALL;

/**
 * 给子应用地址补上 tenant 参数
 * - 当前处于全局租户模式：地址上带 tenant=all（已有 tenant 参数则不重复追加）
 * - 否则：原样返回，不带任何 tenant 参数
 * @param url 子应用地址或子应用内部 hash 路径
 */
export const withTenantParam = (url: string = ""): string => {
  if (!url || !isGlobalTenantMode()) {
    return url;
  }
  if (url.includes(`${GLOBAL_TENANT_QUERY_KEY}=`)) {
    return url;
  }
  // 结尾的斜杠去掉，避免出现 /#/khlinks/?tenant=all 这种空路径带参
  const base = url.endsWith("/") ? url.slice(0, -1) : url;
  return base.includes("?")
    ? `${base}&${GLOBAL_TENANT_QUERY_KEY}=${GLOBAL_TENANT_ALL}`
    : `${base}?${GLOBAL_TENANT_QUERY_KEY}=${GLOBAL_TENANT_ALL}`;
};

type MenuNode = {
  code?: string;
  children?: MenuNode[];
  [key: string]: any;
};

/** 按 code 白名单递归过滤树，命中白名单的节点整棵保留，其祖先节点也会保留 */
const filterTreeByCodes = (
  nodes: MenuNode[] = [],
  codeSet: Set<string>
): MenuNode[] => {
  const result: MenuNode[] = [];
  nodes.forEach((node) => {
    if (!node) {
      return;
    }
    if (node.code && codeSet.has(node.code)) {
      result.push(node);
      return;
    }
    if (node.children?.length) {
      const children = filterTreeByCodes(node.children, codeSet);
      if (children.length) {
        result.push({ ...node, children });
      }
    }
  });
  return result;
};

/**
 * 全局租户模式下的菜单过滤
 * 非全局租户模式时原样返回，调用方可以无条件使用
 */
export const filterGlobalTenantMenus = <T extends MenuNode>(
  menus: T[] = []
): T[] => {
  if (!isGlobalTenantMode()) {
    return menus;
  }

  const rootMenu = menus.find((menu) => menu?.code === GLOBAL_TENANT_ROOT_CODE);
  if (!rootMenu) {
    return [];
  }

  const children = filterTreeByCodes(
    rootMenu.children,
    new Set(GLOBAL_TENANT_MENU_CODES)
  );

  return [{ ...rootMenu, children } as T];
};
